/**
 * Structured logging helper
 *
 * Provides consistent, structured logs across the application with optional
 * external forwarding (e.g., Sentry) and environment-based console control.
 *
 * Usage:
 * const log = createModuleLogger('UserSection');
 * log.info('Duplicate prevented', { sectionDocId, source: 'repo-cache' });
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Default emoji per level for quick visual scanning
const LEVEL_EMOJI: Record<LogLevel, string> = {
  debug: '🐛',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
};

export interface LogContext {
  module?: string; // logical module/domain (Course, Step, UserSection)
  action?: string; // specific action (create, update, fetch)
  [key: string]: unknown; // additional structured fields
}

export interface LogRecord extends LogContext {
  level: LogLevel;
  message: string;
  timestamp: number; // epoch ms for easier processing
  environment: string; // NODE_ENV or 'unknown'
  errorStack?: string;
}

export interface LoggerOptions {
  enableConsoleInProd?: boolean; // force console output in production
  external?: {
    send?: (record: LogRecord) => void | Promise<void>; // external sink (Sentry, etc.)
  };
  filterLevel?: LogLevel; // minimum level to emit (default: debug in dev, info in prod)
}

const env = import.meta.env.QUASAR_DEV ? 'development' : 'production';

// Level precedence for filtering
const levelOrder: LogLevel[] = ['debug', 'info', 'warn', 'error'];

function shouldEmit(level: LogLevel, minLevel: LogLevel): boolean {
  return levelOrder.indexOf(level) >= levelOrder.indexOf(minLevel);
}

class StructuredLogger {
  private options: Required<Omit<LoggerOptions, 'external'>> & {
    external?: LoggerOptions['external'];
    minLevel: LogLevel;
  };

  constructor(opts?: LoggerOptions) {
    const defaultMin = env === 'production' ? 'info' : 'debug';
    this.options = {
      enableConsoleInProd: false,
      filterLevel: opts?.filterLevel ?? defaultMin,
      external: opts?.external,
      minLevel: opts?.filterLevel ?? defaultMin,
    };
  }

  configure(opts: LoggerOptions) {
    if (opts.enableConsoleInProd !== undefined)
      this.options.enableConsoleInProd = opts.enableConsoleInProd;
    if (opts.filterLevel) this.options.minLevel = opts.filterLevel;
    if (opts.external) this.options.external = opts.external;
  }

  createModule(module: string, moduleEmoji?: string) {
    // optional per-module emoji prefix overrides default level emoji when provided
    return {
      debug: (message: string, ctx?: LogContext) =>
        this.log('debug', message, { ...ctx, module, moduleEmoji }),
      info: (message: string, ctx?: LogContext) =>
        this.log('info', message, { ...ctx, module, moduleEmoji }),
      warn: (message: string, ctx?: LogContext) =>
        this.log('warn', message, { ...ctx, module, moduleEmoji }),
      error: (message: string, ctx?: LogContext & { error?: unknown }) =>
        this.log('error', message, { ...ctx, module, moduleEmoji }),
    };
  }

  log(
    level: LogLevel,
    message: string,
    ctx?: LogContext & { error?: unknown },
  ) {
    if (!shouldEmit(level, this.options.minLevel)) return;
    const record: LogRecord = {
      level,
      message,
      timestamp: Date.now(),
      environment: env,
      ...ctx,
    };
    if (ctx?.error instanceof Error) {
      record.errorStack = ctx.error.stack;
    }

    // Console output rules
    const isProd = env === 'production';
    const allowConsole = !isProd || this.options.enableConsoleInProd;
    if (allowConsole) {
      const payload = { ...record };
      if (payload.errorStack && payload.errorStack.length > 5000) {
        payload.errorStack = payload.errorStack.substring(0, 5000) + '…';
      }

      const levelEmoji: string =
        typeof ctx?.moduleEmoji === 'string'
          ? ctx.moduleEmoji
          : LEVEL_EMOJI[level];
      const prefix = `${levelEmoji} ${String(level).toUpperCase()}`;

      switch (level) {
        case 'debug':
          console.debug(prefix, payload.message, payload);
          break;
        case 'info':
          console.info(prefix, payload.message, payload);
          break;
        case 'warn':
          console.warn(prefix, payload.message, payload);
          break;
        case 'error':
          console.error(prefix, payload.message, payload);
          break;
      }
    }

    // External forwarding (non-blocking best effort) - ignore promise intentionally
    try {
      void this.options.external?.send?.(record);
    } catch {
      /* ignore */
    }
  }
}

// Singleton logger instance
export const logger = new StructuredLogger();

// Convenience factory
export function createModuleLogger(module: string, moduleEmoji?: string) {
  return logger.createModule(module, moduleEmoji);
}
